/**
 * Shared "give up and force a clean reload" step for `lazyWithRetry`
 * and `importWithRetry`. Single source of truth so the recovery
 * behavior never drifts between the two call sites (they already
 * duplicated the retry-loop shape verbatim before this extraction).
 *
 * Why a plain `window.location.reload()` is NOT enough: `index.html`
 * is served with `cache-control: public, max-age=0, must-revalidate`,
 * so a normal browser-HTTP-cache staleness is never the cause. The
 * PWA service worker is a SEPARATE interception layer — Workbox's
 * `generateSW` precaches every hashed JS/CSS file at SW-install time
 * with no content-type check. If even one chunk fetch raced a deploy
 * and the CDN served the SPA-fallback `index.html` (200, text/html)
 * instead of a real 404 for that moment, Workbox caches that WRONG
 * response permanently under the JS chunk's own URL key — content-
 * hashed filenames are treated as immutable, so nothing ever
 * revalidates it. A plain reload re-triggers the SAME SW-intercepted
 * fetch and gets the SAME poisoned response back (confirmed in
 * production, 2026-10-18: the `sessionStorage` reload guard showed
 * the error recurring even AFTER the one-time reload already fired).
 *
 * Fix: before reloading, unregister every service worker + clear
 * every Cache Storage entry so the reload is guaranteed to hit the
 * network fresh, bypassing whatever got poisoned. Safe to call
 * unconditionally — idempotent, and Workbox's background-sync queue
 * for offline API mutations lives in IndexedDB (untouched by this),
 * not Cache Storage, so no queued offline writes are lost.
 */
export async function hardReloadBustingStaleCaches(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
    }
    if ('caches' in globalThis) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // Best-effort — even if unregister/clear fails, still reload below
    // so the user isn't stuck on the broken state forever.
  }
  window.location.reload();
}
